import React, { useCallback, useMemo } from "react";
import classnames from "classnames";
import { Icon, Table } from "@shared/components";
import { AlertNotification } from "@shared/models";
import { actions as sharedActions } from "@shared/store";
import { alertNotificationsTableProperties } from "@containers/Dashboard/constants/alertNotificationsTableProperties";
import { HospitalCardEmptyState } from "@containers/Dashboard/components/DashboardHospitalContainer/HospitalCardEmptyState";
import { OVERLAY_TYPES } from "@shared/constants";
import { useDispatch } from "react-redux";
import { ReactComponent as ArrowIcon } from "@assets/images/icons/general/arrow-right-black.svg";

interface AlertNotificationsCardProps {
  alertNotifications: AlertNotification[];
  title: string;
  className?: string;
  signalType: string;
  hospitalId?: number | null;
  emptyState?: string;
  onManageClick: () => void;
  loadNextPage?: () => void;
  onRowClick?: (item: AlertNotification) => void;
}

const AlertNotificationsCard = (props: AlertNotificationsCardProps) => {
  const {
    title,
    className,
    onManageClick,
    alertNotifications,
    loadNextPage,
    emptyState,
    onRowClick,
    signalType,
    hospitalId,
  } = props;

  const dispatch = useDispatch();

  const isEmpty = useMemo(() => !alertNotifications.length, [alertNotifications]);

  const notifications = useMemo(() => {
    const MAX_NOTIFICATIONS = 7;
    return alertNotifications.slice(0, MAX_NOTIFICATIONS);
  }, [alertNotifications]);

  const openAlertEventsListOverlay = useCallback(() => {
    dispatch(
      sharedActions.showOverlay({
        type: OVERLAY_TYPES.ALERT_NOTIFICATIONS_LIST,
        closeOnOverlayClick: true,
        props: {
          signalType,
          hospitalId,
          onRowClick,
        },
      }),
    );
  }, [dispatch, signalType, hospitalId, onRowClick]);

  return (
    <div className={classnames("alert-notifications-card", className)}>
      <div className={classnames("hospital-dashboard-card-title", { "hospital-dashboard-card-empty": isEmpty })}>
        <div className="alert-notifications-card-title-content">{title}</div>
        <div className="hospital-dashboard-card-title-action">
          <Icon type="settings" onClick={onManageClick} />
          <div className="show-all-notifications" onClick={openAlertEventsListOverlay}>
            View All <ArrowIcon />
          </div>
        </div>
      </div>
      <div className="hospital-dashboard-card-content">
        {!isEmpty ? (
          <Table<AlertNotification>
            items={notifications}
            properties={alertNotificationsTableProperties}
            onFinishScroll={loadNextPage}
            onRowClick={onRowClick}
          />
        ) : (
          <HospitalCardEmptyState title={emptyState} />
        )}
      </div>
    </div>
  );
};

export default AlertNotificationsCard;
